import type { DataOption } from '@/common.types'
import type { UseControllerProps } from 'react-hook-form'

import React from 'react'
import { HelpCircleIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import {
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import { Field, FieldContent, FieldLabel, FieldTitle } from '../ui/field'

export default function RHFRadioGroup({
	name,
	label,
	shouldUnregister,
	defaultValue,
	options,
	disabled,
	hint,
	description,
}: UseControllerProps & {
	label: string
	hint?: string
	description?: string
	disabled?: boolean
	options: DataOption[]
}) {
	const { control } = useFormContext()

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field }) => (
				<FormItem className='gap-0'>
					<FormLabel>
						{label}
						{hint && (
							<Tooltip>
								<TooltipTrigger asChild>
									<HelpCircleIcon className='size-3 text-muted-foreground' />
								</TooltipTrigger>
								<TooltipContent side='top'>{hint}</TooltipContent>
							</Tooltip>
						)}
					</FormLabel>
					{!!description && <FormDescription>{description}</FormDescription>}
					<RadioGroup
						onValueChange={field.onChange}
						defaultValue={field.value}
						className='mt-1.5 flex flex-col gap-2'
					>
						{options.map((option) => (
							<FieldLabel key={option.value}>
								<Field orientation='horizontal'>
									<RadioGroupItem value={option.value} disabled={disabled} />
									<FieldContent>
										<FieldTitle>{option.label}</FieldTitle>
									</FieldContent>
								</Field>
							</FieldLabel>
						))}
					</RadioGroup>

					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
